import type { Maybe } from '@adornis/base/utilTypes.js';
import { Int } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';

/** Woher das Lieferdatum stammt. */
export type DeliveryDateSource = 'fahrplan' | 'abholer' | 'ersatzregel';

/** Warum ein an sich passender Abfahrtstag ausfiel. */
export type SkipReason = 'holiday' | 'shutdown' | 'cutoff';

/** Ein Abfahrtstag, der im Fahrplan stand, aber nicht genommen werden konnte. */
export interface SkippedDeparture {
  /** `yyyy-MM-dd`. */
  date: string;
  reason: SkipReason;
}

/**
 * Übersprungene Abfahrtstage werden als `yyyy-MM-dd:grund` abgelegt, nicht als eigene Entity.
 *
 * Der Grund ist die Tiefe des Selection Sets: `MailExtractionResult.allFields` reicht zwei
 * Ebenen tief. Eine Entity in diesem Feld läge auf der dritten und käme nirgends mit an — beim
 * Lesen wirft der Zugriff, und beim Zurückspeichern eines so geladenen Ergebnisses wäre sie
 * stillschweigend weg. Zwei Werte aus festem Vorrat brauchen dafür keine eigene Tabelle.
 */
export function encodeSkipped(skipped: readonly SkippedDeparture[]): string[] {
  return skipped.map(s => `${s.date}:${s.reason}`);
}

const SKIP_REASONS: readonly SkipReason[] = ['holiday', 'shutdown', 'cutoff'];

/** Gegenstück zu {@link encodeSkipped}. Unlesbare Einträge fallen heraus, statt zu raten. */
export function decodeSkipped(encoded: Maybe<readonly string[]>): SkippedDeparture[] {
  return (encoded ?? []).flatMap(eintrag => {
    const [date, reason] = eintrag.split(':');
    if (!date || !reason) return [];
    return SKIP_REASONS.includes(reason as SkipReason) ? [{ date, reason: reason as SkipReason }] : [];
  });
}

/**
 * Die Herleitung des berechneten Lieferdatums, mitgeschrieben für die Anzeige.
 *
 * Ein Termin, der niemand erklären kann, wird im Zweifel überschrieben — deshalb steht neben
 * dem Datum, welche Zone, welcher Abfahrtstag und welche Transportzeit dazu geführt haben.
 * Bei der Ersatzregel sagt `reason`, warum kein Fahrplan gegriffen hat.
 */
@Entity()
export class DeliveryDateBasis extends AdornisEntity {
  static override _class = 'DeliveryDateBasis';

  @Field(() => String) source: Maybe<DeliveryDateSource>;

  /** Zonencode des Kunden, auch wenn die Zone keinen Fahrplaneintrag hat. */
  @Field(() => String) transportZone: Maybe<string>;
  @Field(() => String) zoneLabel: Maybe<string>;

  /** Abfahrtstag als `yyyy-MM-dd`. Leer bei Selbstabholern und bei der Ersatzregel. */
  @Field(() => String) departureDate: Maybe<string>;

  /** Transportzeit in Werktagen: 24 h = 1, 48 h = 2, 72 h = 3. */
  @Field(() => Int) transitDays: Maybe<number>;

  /** Warum die Ersatzregel greifen musste. Leer, wenn der Fahrplan gegriffen hat. */
  @Field(() => String) fallbackReason: Maybe<string>;

  /**
   * Zeitpunkt, ab dem gerechnet wurde — ISO in Berliner Zeit. Der Anfang der Herleitung.
   *
   * Bewusst der Moment der Verarbeitung und nicht der Eingang der Mail: Eine Anfrage kann
   * Wochen liegen, bevor sie verarbeitet wird, und der Eingangszeitpunkt ergäbe dann einen
   * Termin, der längst vorbei ist.
   */
  @Field(() => String) calculatedAt: Maybe<string>;

  /** Die Abfahrtstage der Zone (1 = Mo … 5 = Fr), damit der Fahrplan mit angezeigt werden kann. */
  @Field(() => [Int]) departureWeekdays: Maybe<number[]>;

  /**
   * Abfahrtstage, die zwischen Start und gewählter Abfahrt ausfielen — je Eintrag
   * `yyyy-MM-dd:grund`. Lesen über {@link decodeSkipped}.
   */
  @Field(() => [String]) skipped: Maybe<string[]>;

  constructor(data: Partial<DeliveryDateBasis>) {
    super(data);
    Object.assign(this, data);
  }
}
